import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { ContactGroup, WorkspaceFilter } from "@/lib/support-workspace";

const PAGE_SIZE = 50;

export type SupportConversationSearchResult = {
  issue_id: string;
  comment_id: string | null;
  score: number;
  snippet: string;
};

export type SupportContactSearchResult = {
  recipient_kind: "booking" | "lessor";
  recipient_id: string;
  contact_name: string;
  contact_detail: string;
  score: number;
};

export function useDebouncedValue(value: string, delay = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debounced;
}

export function supportSearchDatabaseScope(filter: WorkspaceFilter): {
  filterScope: string;
  assigneeScope: string | null;
} {
  if (filter.startsWith("person:")) {
    return { filterScope: "person", assigneeScope: filter.slice(7) };
  }
  return {
    filterScope: filter === "activity" ? "all" : filter,
    assigneeScope: null,
  };
}

export function useSupportSearch(input: {
  query: string;
  group: ContactGroup;
  filter: WorkspaceFilter;
}) {
  const query = useDebouncedValue(input.query.trim(), 250);
  const scope = useMemo(
    () => supportSearchDatabaseScope(input.filter),
    [input.filter]
  );
  const client = supabase as unknown as {
    rpc: (
      name: string,
      args: Record<string, unknown>
    ) => Promise<{ data: unknown; error: { message: string } | null }>;
  };

  const conversations = useInfiniteQuery({
    queryKey: ["support-search", "conversations", query, input.group, scope],
    enabled: query.length > 0 && input.filter !== "activity",
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.rpc("search_support_conversations", {
        search_query: query,
        contact_scope: input.group === "all" ? null : input.group,
        filter_scope: scope.filterScope,
        assignee_scope: scope.assigneeScope,
        result_limit: PAGE_SIZE,
        result_offset: Number(pageParam) * PAGE_SIZE,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as SupportConversationSearchResult[];
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === PAGE_SIZE ? pages.length : undefined,
  });

  const contacts = useQuery({
    queryKey: ["support-search", "contacts", query],
    enabled:
      query.length > 0 && input.group === "all" && input.filter === "all",
    queryFn: async () => {
      const { data, error } = await client.rpc("search_support_contacts", {
        search_query: query,
        result_limit: 12,
        result_offset: 0,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as SupportContactSearchResult[];
    },
  });

  return {
    query,
    conversationResults: conversations.data?.pages.flat() ?? [],
    contacts: contacts.data ?? [],
    isLoading: conversations.isLoading || contacts.isLoading,
    error: conversations.error ?? contacts.error,
    hasMore: conversations.hasNextPage,
    loadMore: conversations.fetchNextPage,
    isLoadingMore: conversations.isFetchingNextPage,
  };
}
